/**
 * buildAnalyticsReportPdfModel.js
 *
 * Stage 10 — builds a plain-data model for one Saved Report, ready for
 * AnalyticsReportPdfRenderer.js to turn into print-ready HTML. Same
 * division of responsibility as buildCostControlPdfModel.js: this
 * function ONLY shapes already-computed data into a display structure —
 * it performs NO financial calculation. The QueryResult/TimeSeriesResult
 * it receives has already been produced by executeAnalyticalOutput()
 * (queryDispatcher.js), exactly the same object the Live Preview
 * rendered on screen — the PDF and the Preview are guaranteed to show
 * identical numbers because they read the same result, never two
 * independently-executed queries.
 */

import { getMeasureMetadata, DATASETS } from './analyticalQuery'
import { COMPOSITE_OUTPUTS } from './compositeOutputs'

/**
 * @param {SavedReport} report - the full row from saved_reports (or an
 *   unsaved draft with the same shape), including analytical_definition/
 *   visualization/presentation
 * @param {QueryResult|TimeSeriesResult} result - the ALREADY-EXECUTED
 *   result (same object the Live Preview/Viewer rendered — never
 *   re-executed or recomputed here)
 * @param {object} [options] - orgName, logoUrl, generatedBy, generatedAt
 */
export function buildAnalyticsReportPdfModel(report, result, options = {}) {
  const { analytical_definition, visualization, presentation } = report
  const outputType = analytical_definition.outputType || 'dataset'

  const datasetLabel = outputType === 'composite'
    ? COMPOSITE_OUTPUTS[analytical_definition.compositeKey]?.label
    : DATASETS[analytical_definition.query.dataset]?.label

  const measures = outputType === 'composite'
    ? COMPOSITE_OUTPUTS[analytical_definition.compositeKey].fixedSeries
    : analytical_definition.query.measures

  const filterSummary = outputType === 'dataset'
    ? Object.entries(analytical_definition.query.filters || {})
        .filter(([, v]) => v != null && v !== 'all')
        .map(([key, value]) => ({
          key,
          label: formatFilterKeyLabel(key),
          value: Array.isArray(value) ? value.join(', ') : value,
        }))
    : []

  const tableRows = flattenResultToRows(result, measures)

  return {
    header: {
      orgName: options.orgName || 'CostSpace',
      logoUrl: options.logoUrl || null,
      reportTitle: presentation.title,
      datasetLabel,
      generatedAt: options.generatedAt || new Date().toISOString(),
      generatedBy: options.generatedBy || null,
    },
    metadata: {
      datasetLabel,
      filterSummary,
      dimension: outputType === 'dataset' ? analytical_definition.query.dimension : null,
      visualType: visualization.type,
    },
    measures: measures.map(m => ({ key: m, label: getMeasureMetadata(m).label, format: getMeasureMetadata(m).format })),
    tableRows,
    notes: presentation.notes || null,
  }
}

function formatFilterKeyLabel(key) {
  const labels = {
    cost_type: 'Cost Type', resource_type: 'Resource Type',
    indirect_category: 'Indirect Category', sub_code: 'Sub Code', resource_code: 'Resource Code',
  }
  return labels[key] || key
}

function flattenResultToRows(result, measures) {
  if (result.rows) {
    return result.rows.map(r => ({
      label: r.label,
      values: measures.map(m => r.values[m]),
    }))
  }
  if (result.series) {
    return result.series.flatMap(point =>
      point.rows.map(r => ({
        label: `${r.label} — ${point.time}`,
        values: measures.map(m => r.values[m]),
      }))
    )
  }
  return []
}
